import { ChangeDetectionStrategy, Component, computed, effect, HostListener, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ClientSessionService } from '@brighthub/uisession';
import { AccordionModule } from 'primeng/accordion';
import { ButtonDirective } from 'primeng/button';
import { Tooltip } from 'primeng/tooltip';
import { ClipboardService } from '@brighthub/utils';
import { CapitalizeFirstPipe } from '@brighthub/helpers';
import { AmplitudeService } from '@brighthub/logging';
import { AuthService } from '@brightside/core/auth';
import { ClientAccountsStateService } from '../client-accounts/client-accounts-state.service';
import { ClientContactService } from '../client-contact/client-contact.service';
import { ClientAiInsightsStateService } from '../client-ai-insights/client-ai-insights-state.service';

const USD = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

// Deterministic seeding helpers (same djb2 pattern used across the app)
function djb2(str: string): number {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 33) ^ str.charCodeAt(i);
  }
  return hash >>> 0;
}

function seedPick<T>(id: string, key: string, options: readonly T[]): T {
  return options[djb2(`${id}:${key}`) % options.length];
}

const FIRST_NAMES = [
  'Emma', 'Liam', 'Olivia', 'Noah', 'Ava', 'Elijah', 'Sophia', 'James',
  'Isabella', 'Oliver', 'Mia', 'Benjamin', 'Charlotte', 'Lucas', 'Amelia',
  'Mason', 'Harper', 'Ethan', 'Evelyn', 'Aiden',
] as const;

const LAST_NAMES = [
  'Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Garcia', 'Miller',
  'Davis', 'Rodriguez', 'Martinez', 'Hernandez', 'Lopez', 'Gonzalez',
  'Wilson', 'Anderson', 'Thomas', 'Taylor', 'Moore', 'Jackson', 'Martin',
] as const;

@Component({
  selector: 'bai-client-metadata',
  imports: [
    CommonModule,
    FormsModule,
    AccordionModule,
    ButtonDirective,
    Tooltip,
    CapitalizeFirstPipe,
  ],
  templateUrl: './client-metadata.component.html',
  styleUrl: './client-metadata.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClientMetadataComponent {
  private clientSessionService = inject(ClientSessionService);
  private _clipboardService = inject(ClipboardService);
  private _amplitude = inject(AmplitudeService);
  private _accountsState = inject(ClientAccountsStateService);
  private _auth = inject(AuthService);
  private _contactService = inject(ClientContactService);
  private _insightsState = inject(ClientAiInsightsStateService);

  readonly subjectData = this.clientSessionService.selectedSubjectData;
  readonly session = this.clientSessionService.selectedSession;

  readonly contact = computed(() => {
    const s = this.subjectData();
    return s ? this._contactService.resolveContact(s) : null;
  });

  // ---------------------------------------------------------------------------
  // Quick-action header state
  // ---------------------------------------------------------------------------

  readonly showMoreMenu = signal(false);

  // Note modal state
  readonly noteModalOpen = signal(false);
  readonly noteTitle = signal('');
  readonly noteBody = signal('');
  readonly noteTimestamp = signal('');
  readonly noteCanSubmit = computed(() => this.noteBody().trim().length > 0);

  @HostListener('document:click')
  onDocumentClick(): void {
    this.showMoreMenu.set(false);
  }

  toggleMoreMenu(event: Event): void {
    event.stopPropagation();
    this.showMoreMenu.update((v) => !v);
  }

  openNoteModal(event: Event): void {
    event.stopPropagation();
    this.showMoreMenu.set(false);
    this.noteTitle.set('');
    this.noteBody.set('');
    this.noteTimestamp.set(new Date().toISOString().slice(0, 16));
    this.noteModalOpen.set(true);
  }

  closeNoteModal(): void {
    this.noteModalOpen.set(false);
  }

  submitNote(): void {
    const sessionId = this.session()?.uiSessionId;
    if (!sessionId || !this.noteCanSubmit()) return;
    this._insightsState.ensureState(sessionId);
    const ts = this.noteTimestamp()
      ? new Date(this.noteTimestamp()).toISOString()
      : new Date().toISOString();
    this._insightsState.addManualNote(sessionId, this.noteTitle(), this.noteBody(), ts);
    this.closeNoteModal();
  }

  constructor() {
    effect(() => {
      const data = this.subjectData();
    });
  }

  readonly tenureString = computed(() => {
    const s = this.subjectData();
    if (!s?.hireDate) return '—';

    const start = new Date(s.hireDate);
    const now = new Date();

    const years = now.getFullYear() - start.getFullYear();
    const months = now.getMonth() - start.getMonth();
    const days = now.getDate() - start.getDate();

    const totalMonths = years * 12 + months + (days < 0 ? -1 : 0);

    const duration: string[] = [];

    if (totalMonths >= 12) {
      const y = Math.floor(totalMonths / 12);
      const m = totalMonths % 12;
      duration.push(`${y} year${y > 1 ? 's' : ''}`);
      if (m > 0) duration.push(`${m} month${m > 1 ? 's' : ''}`);
    } else if (totalMonths > 0) {
      duration.push(`${totalMonths} month${totalMonths > 1 ? 's' : ''}`);
    } else {
      const diffTime = Math.abs(now.getTime() - start.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      duration.push(`${diffDays} day${diffDays > 1 ? 's' : ''}`);
    }

    return duration.join(' and ');
  });

  readonly age = computed(() => {
    const s = this.subjectData();
    const dobString = s?.dateOfBirth;
    if (!dobString) return null;

    const [year, month, day] = dobString.split('-').map(Number);
    if (!year || !month || !day) return null;

    const dob = new Date(year, month - 1, day);
    const today = new Date();

    let age = today.getFullYear() - dob.getFullYear();
    const hasHadBirthdayThisYear =
      today.getMonth() > dob.getMonth() ||
      (today.getMonth() === dob.getMonth() && today.getDate() >= dob.getDate());

    if (!hasHadBirthdayThisYear) {
      age--;
    }

    return age;
  });

  /**
   * Infers conventional pronouns from gender string.
   * Returns null when gender is absent or unrecognised (so nothing is shown).
   */
  readonly inferredPronouns = computed((): string | null => {
    const explicit = this.subjectData()?.pronouns;
    if (explicit) return explicit;

    const gender = (this.subjectData()?.gender ?? '').toLowerCase().trim();
    if (!gender) return null;

    if (['female', 'woman', 'girl', 'f'].includes(gender)) return 'she/her';
    if (['male', 'man', 'boy', 'm'].includes(gender)) return 'he/him';
    if (['non-binary', 'nonbinary', 'non binary', 'enby', 'genderqueer', 'genderfluid'].includes(gender)) return 'they/them';
    return null;
  });

  /** Demo: use the logged-in FA's display name as the assigned FA. */
  readonly assignedFa = computed(() => {
    const fromData = this.subjectData()?.assignedFaName;
    if (fromData) return fromData;
    const displayName = this._auth.userDisplayName();
    return displayName || '—';
  });

  readonly firstInteraction = computed(() => {
    const d = this.subjectData()?.firstInteractionDate ?? this.session()?.createdDate;
    return d
      ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
      : '—';
  });

  /** Demo: always show today's date as the most recent interaction. */
  readonly mostRecentInteraction = computed(() => {
    const d = this.subjectData()?.mostRecentInteractionDate;
    const date = d ? new Date(d) : new Date();
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  });

  /**
   * Demo: two deterministic dependent names seeded from the client GUID.
   * Returns null when no client is selected.
   */
  readonly householdDependentNames = computed((): string[] | null => {
    const guid = this.subjectData()?.guid;
    if (!guid) return null;
    const first1 = seedPick(guid, 'dep_first_0', FIRST_NAMES);
    const last1  = seedPick(guid, 'dep_last_0',  LAST_NAMES);
    const first2 = seedPick(guid, 'dep_first_1', FIRST_NAMES);
    const last2  = seedPick(guid, 'dep_last_1',  LAST_NAMES);
    return [`${first1} ${last1}`, `${first2} ${last2}`];
  });

  readonly spendingBalance = computed(() => {
    const id = this.session()?.uiSessionId;
    if (!id) return null;
    const state = this._accountsState.storeSnapshot()[id];
    return state?.assets.find(a => a.category === 'checking')?.balance ?? null;
  });

  readonly savingsBalance = computed(() => {
    const id = this.session()?.uiSessionId;
    if (!id) return null;
    const state = this._accountsState.storeSnapshot()[id];
    return state?.assets.find(a => a.category === 'savings')?.balance ?? null;
  });

  formatUsd(v: number): string {
    return USD.format(v);
  }

  copyToClipboard(guid: string) {
    this._clipboardService.copy(guid);
  }

  openLink(url: string): void {
    if (!url) return;

    this._amplitude.logEvent('openLink', {
      subjectId: this.subjectData()?.guid ?? 'unknown',
      url
    });
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  callClient(event?: Event): void {
    event?.stopPropagation();
    const c = this.contact();
    if (!c) return;
    this._contactService.openPhone(c.primaryPhone, 'metadata_links', this.subjectData()?.guid);
  }

  emailClient(event?: Event): void {
    event?.stopPropagation();
    const c = this.contact();
    if (!c) return;
    this._contactService.openEmail(c.primaryEmail, 'metadata_links', this.subjectData()?.guid);
  }
}

